import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { isRouteType } from "../../shared/stepConfig";
import { useQuery } from "../hooks/useQuery";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import { changedRouteAccess } from "../lib/routeAccessChanges";
import {
  ROUTE_AUTH_BADGE_COLORS,
  routeAuthDescription,
  routeAuthLabel,
} from "../lib/routeAuthDisplay";
import { ROUTE_TYPE_ICONS } from "../lib/routeTypeDisplay";
import { stepDirsWithStepTests } from "../lib/stepTestDirs";
import {
  computeStepTestFailures,
  STEP_TEST_TIMEOUT_MS,
  type StepRunStatus,
  stepRunStatus,
  type TestExecution,
} from "../lib/stepTestFailures";
import { makeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Link, isInAppNavigation } from "./Link";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter, ModalHead } from "./ModalCard";
import { PreviewListBox } from "./PreviewList";
import { Spinner } from "./Spinner";
import { StatusBadge } from "./StatusBadge";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const StepTestRow = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
`;

const RowHead = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameSlot = styled("div")`
  flex: 1;
  min-width: 0;
`;

const StatusSlot = styled("div")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const FailedRow = styled(Link)`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }
`;

const ChangedRouteRow = styled("a")`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }
`;

const AuthChange = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

function StepStatusIcon({ status }: { status: StepRunStatus }) {
  if (status === "running") return <Spinner size={16} />;
  if (status === "passed")
    return <Icon name="check-filled" size={16} color="var(--green-600)" />;
  return <Icon name="check-filled-x" size={16} color="var(--red-600)" />;
}

type Props = {
  workflowId: string;
  branchId: string;
  spaceId: string;
  onProceed: () => void;
  onCancel: () => void;
};

// Two independent gates run before a push, each its own modal: first the step
// tests (silent when they pass), then a confirmation for any link whose access
// scope changed. Each stage advances itself when it has nothing to show, so a
// clean push flashes neither.
export function PushLivePreflight({
  workflowId,
  branchId,
  spaceId,
  onProceed,
  onCancel,
}: Props) {
  const [stage, setStage] = useState<"tests" | "access">("tests");
  const goToAccess = useCallback(() => setStage("access"), []);

  if (stage === "tests") {
    return (
      <StepTestsGate
        workflowId={workflowId}
        branchId={branchId}
        spaceId={spaceId}
        onProceed={goToAccess}
        onCancel={onCancel}
      />
    );
  }
  return (
    <AccessChangeGate
      workflowId={workflowId}
      branchId={branchId}
      spaceId={spaceId}
      onProceed={onProceed}
      onCancel={onCancel}
    />
  );
}

function StepTestsGate({
  workflowId,
  branchId,
  spaceId,
  onProceed,
  onCancel,
}: {
  workflowId: string;
  branchId: string;
  spaceId: string;
  onProceed: () => void;
  onCancel: () => void;
}) {
  const z = useZero();

  // Read the branch's head commit from the same query the workflow page uses,
  // so the files (and the commit id we run examples against) are the live head.
  const [branch, branchResult] = useQuery(
    queries.loadBranch({ workflowId, branchId })
  );
  const headCommit = branch?.headCommit ?? null;
  const commitId = headCommit?.id ?? null;
  const testStepDirs = useMemo(
    () => stepDirsWithStepTests(headCommit?.files ?? []),
    [headCommit?.files]
  );

  const [runs, setRuns] = useState<
    { stepDir: string; executionId: string }[] | null
  >(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedRef = useRef(false);
  const proceededRef = useRef(false);

  // Once the branch's files have loaded, kick off a fresh step-test run per step
  // that has step tests. No step tests → nothing to gate, advance immediately.
  useEffect(() => {
    if (startedRef.current) return;
    if (branchResult.type !== "complete") return;
    startedRef.current = true;
    if (!commitId || testStepDirs.length === 0) {
      onProceed();
      return;
    }
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setRuns(
      testStepDirs.map((stepDir) => {
        const executionId = nanoid(12);
        z.mutate(
          sharedMutators.runStepTests({
            runId: nanoid(12),
            workflowId,
            commitId,
            stepDir,
            executionId,
          })
        );
        return { stepDir, executionId };
      })
    );
  }, [branchResult.type, testStepDirs, commitId, z, workflowId, onProceed]);

  useEffect(() => {
    if (!runs) return;
    const t = setTimeout(() => setTimedOut(true), STEP_TEST_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [runs]);

  const { executions: testExecutions } = useStepTestExecutions(
    workflowId,
    branchId
  );
  const byId = useMemo(() => {
    const map = new Map<string, TestExecution>();
    for (const e of testExecutions) map.set(e.id, e);
    return map;
  }, [testExecutions]);

  const execs = runs?.map((r) => byId.get(r.executionId)) ?? [];
  const total = runs?.length ?? 0;
  const completed = execs.filter((e) => e && e.status !== "pending").length;
  const allTerminal = runs != null && completed === total;

  // Spec rows commit in the same transaction as the execution's terminal
  // status, so once every run is terminal we can judge immediately.
  const failures =
    runs && (allTerminal || timedOut)
      ? computeStepTestFailures(runs, byId, timedOut)
      : null;

  useEffect(() => {
    if (proceededRef.current) return;
    if (failures && failures.length === 0) {
      proceededRef.current = true;
      onProceed();
    }
  }, [failures, onProceed]);

  return (
    <Modal open onClose={onCancel} alert>
      <ModalContent style={{ width: 320 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Step tests
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            We run these on the latest version before pushing live.
          </Text>
        </ModalHead>
        <PreviewListBox style={{ maxHeight: 280 }}>
          {runs && runs.length > 0 ? (
            runs.map((r) => {
              const status = stepRunStatus(r, byId);
              const rowContent = (
                <>
                  <NameSlot>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {r.stepDir}
                    </Text>
                  </NameSlot>
                  <StatusSlot>
                    <StepStatusIcon status={status} />
                  </StatusSlot>
                </>
              );
              if (status === "failed") {
                const testName =
                  byId
                    .get(r.executionId)
                    ?.testSpecs.find((s) => s.status === "failed")?.name ?? "";
                return (
                  <FailedRow
                    key={r.stepDir}
                    to={makeUrl(routes.space.workflow, {
                      spaceId,
                      workflowId,
                      branchId,
                      step: r.stepDir,
                      stepTest: testName,
                      view: "output",
                    })}
                    onMouseDown={(e) => {
                      if (isInAppNavigation(e)) onCancel();
                    }}
                  >
                    {rowContent}
                  </FailedRow>
                );
              }
              return (
                <StepTestRow key={r.stepDir}>
                  <RowHead>{rowContent}</RowHead>
                </StepTestRow>
              );
            })
          ) : (
            <StepTestRow>
              <RowHead>
                <NameSlot>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    Preparing…
                  </Text>
                </NameSlot>
                <StatusSlot>
                  <Spinner size={16} />
                </StatusSlot>
              </RowHead>
            </StepTestRow>
          )}
        </PreviewListBox>
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          {failures != null && failures.length > 0 && (
            <Button size="l" onClick={onProceed}>
              Push live anyway
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function AccessChangeGate({
  workflowId,
  branchId,
  spaceId,
  onProceed,
  onCancel,
}: {
  workflowId: string;
  branchId: string;
  spaceId: string;
  onProceed: () => void;
  onCancel: () => void;
}) {
  const [draft, draftResult] = useQuery(
    queries.loadBranch({ workflowId, branchId })
  );
  // Resolve the live scope from the workflow's main branch directly, so a query
  // that completes with no main branch (a first publish) is distinguishable
  // from one that simply hasn't loaded — the gate must not fail open.
  const [mainWorkflow, mainResult] = useQuery(
    queries.workflowMainRoutes({ workflowId })
  );

  const changes = useMemo(
    () =>
      changedRouteAccess(
        draft?.headCommit?.routes ?? [],
        mainWorkflow?.branches?.headCommit?.routes ?? []
      ),
    [draft?.headCommit?.routes, mainWorkflow?.branches?.headCommit?.routes]
  );

  const errored = draftResult.type === "error" || mainResult.type === "error";
  const resolved =
    draftResult.type === "complete" && mainResult.type === "complete";
  const warn = resolved && changes.length > 0;

  // A query error can't be recovered here, so proceed rather than trap the push
  // behind a gate that would never resolve.
  const proceededRef = useRef(false);
  useEffect(() => {
    if (proceededRef.current) return;
    if (!errored && !(resolved && !warn)) return;
    proceededRef.current = true;
    onProceed();
  }, [errored, resolved, warn, onProceed]);

  if (errored) return null;

  if (!resolved) {
    return (
      <Modal open onClose={onCancel} alert>
        <ModalContent style={{ width: 320 }}>
          <ModalHead>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Before you push live
            </Text>
          </ModalHead>
          <PreviewListBox style={{ maxHeight: 280 }}>
            <StepTestRow>
              <RowHead>
                <NameSlot>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    Checking link access…
                  </Text>
                </NameSlot>
                <StatusSlot>
                  <Spinner size={16} />
                </StatusSlot>
              </RowHead>
            </StepTestRow>
          </PreviewListBox>
          <ModalFooter>
            <Button size="l" variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  if (!warn) return null;

  return (
    <Modal open onClose={onCancel} alert>
      <ModalContent style={{ width: 320 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Before you push live
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            Access to these links changed in this draft.
          </Text>
        </ModalHead>
        <PreviewListBox style={{ maxHeight: 280 }}>
          {changes.map(({ route, stepDir, title, routeType, from, to }) => {
            const iconName = isRouteType(routeType)
              ? ROUTE_TYPE_ICONS[routeType]
              : "link-2";
            return (
              <ChangedRouteRow
                key={route}
                href={makeUrl(routes.space.workflow, {
                  spaceId,
                  workflowId,
                  branchId,
                  step: stepDir,
                })}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StatusSlot>
                  <Icon name={iconName} size={16} color="var(--text-primary)" />
                </StatusSlot>
                <NameSlot>
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {title || route}
                  </Text>
                </NameSlot>
                <AuthChange>
                  {from === null ? (
                    <Tooltip content="This link is new in this draft.">
                      <StatusBadge color="gray">New</StatusBadge>
                    </Tooltip>
                  ) : (
                    <Tooltip content={routeAuthDescription(from)}>
                      <StatusBadge color={ROUTE_AUTH_BADGE_COLORS[from]}>
                        {routeAuthLabel(from)}
                      </StatusBadge>
                    </Tooltip>
                  )}
                  <Text variant="text-small" color="var(--text-tertiary)">
                    →
                  </Text>
                  <Tooltip content={routeAuthDescription(to)}>
                    <StatusBadge color={ROUTE_AUTH_BADGE_COLORS[to]}>
                      {routeAuthLabel(to)}
                    </StatusBadge>
                  </Tooltip>
                </AuthChange>
              </ChangedRouteRow>
            );
          })}
        </PreviewListBox>
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="l" onClick={onProceed}>
            Push live
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
